import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import Pagination, { useClientPagination } from "../components/Pagination";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  Mail,
  Send,
  CheckCircle2,
  XCircle,
  Clock,
  Filter,
  RefreshCw,
  Loader2,
  User as UserIcon,
  Activity,
  Shield,
} from "lucide-react";

const CATEGORY_META = {
  usuarios: { label: "Usuarios", icon: UserIcon, color: "#0A3747" },
  operativas: { label: "Operativas", icon: Activity, color: "#E0E621" },
  administrativas: { label: "Administrativas", icon: Shield, color: "#92400E" },
};

const STATUS_BADGE = {
  sent: { label: "Enviado", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  queued: { label: "En cola", color: "bg-blue-100 text-blue-800", icon: Clock },
  failed: { label: "Falló", color: "bg-red-100 text-red-800", icon: XCircle },
};

export default function Notificaciones() {
  const { user, hasPerm } = useAuth();
  const [types, setTypes] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ sent: 0, queued: 0, failed: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);
  const [filterCat, setFilterCat] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [testEmail, setTestEmail] = useState(user?.email || "");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [t, l] = await Promise.all([
        api.get("/v1/notifications/types"),
        api.get("/v1/notifications/log", { params: { limit: 100, category: filterCat || undefined, status: filterStatus || undefined } }),
      ]);
      setTypes(t.data.items || []);
      setLogs(l.data.items || []);
      setStats(l.data.stats || { sent: 0, queued: 0, failed: 0, total: 0 });
    } catch (e) {
      toast.error("No se pudo cargar la información de notificaciones");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [filterCat, filterStatus]);

  const sendTest = async (type) => {
    if (!testEmail) {
      toast.error("Ingresa un email de destino");
      return;
    }
    setSending(type);
    try {
      await api.post("/v1/notifications/test", { notification_type: type, to_email: testEmail });
      toast.success(`Correo "${type}" encolado para ${testEmail}`);
      setTimeout(fetchAll, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al enviar prueba");
    } finally {
      setSending(null);
    }
  };

  // v97.6.0 · Diagnóstico SMTP: envía YA (sin cola) y muestra el error exacto del servidor de correo
  const [smtpDiag, setSmtpDiag] = useState(null);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const testSmtp = async () => {
    if (!testEmail) { toast.error("Ingresa un email de destino"); return; }
    setSmtpTesting(true); setSmtpDiag(null);
    try {
      const { data } = await api.post("/v1/notifications/test-smtp", { to_email: testEmail });
      setSmtpDiag(data);
      if (data.ok) toast.success("Correo de prueba enviado. Revisa la bandeja (y Spam).");
      else toast.error("El servidor de correo rechazó el envío. Ver detalle.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      setSmtpDiag({ ok: false, error: typeof d === "string" ? d : "Error al probar SMTP" });
    } finally { setSmtpTesting(false); }
  };

  const grouped = types.reduce((acc, t) => {
    if (!acc[t.category]) acc[t.category] = [];
    acc[t.category].push(t);
    return acc;
  }, {});

  const pgLogs = useClientPagination(logs, 10);

  return (
    <Layout title="Notificaciones por correo" subtitle="Configuración · M17">
      <div className="space-y-6" data-testid="notifications-page">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total enviados" value={stats.sent} color="text-green-700" />
          <StatCard label="En cola" value={stats.queued} color="text-blue-700" />
          <StatCard label="Fallidos" value={stats.failed} color="text-red-700" />
          <StatCard label="Total registros" value={stats.total} color="text-gray-900" />
        </div>

        {/* Test email input */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-start gap-4 flex-wrap">
            <Mail className="text-[#0A3747] mt-1" size={24} />
            <div className="flex-1 min-w-[280px]">
              <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: "Chivo" }}>Enviar correo de prueba</h2>
              <p className="text-sm text-gray-600 mt-1">Selecciona el tipo de notificación y se enviará a la dirección que indiques con datos de ejemplo.</p>
            </div>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="destino@empresa.com"
              className="px-3 py-2 border border-gray-300 rounded-md text-sm w-full sm:w-72"
              data-testid="test-email-input"
            />
            <button onClick={testSmtp} disabled={smtpTesting}
              className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: "#0A3747" }} data-testid="btn-test-smtp">
              {smtpTesting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Probar servidor de correo
            </button>
          </div>
          {smtpDiag && (
            <div className={`mt-3 rounded-md px-3 py-2 text-xs font-mono ${smtpDiag.ok ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`}
              data-testid="smtp-diag">
              <div className="font-bold">{smtpDiag.ok ? "OK · " + (smtpDiag.message || "Enviado") : "ERROR · " + (smtpDiag.error || "")}</div>
              {smtpDiag.diag && (
                <div className="mt-1 text-gray-700">
                  host {smtpDiag.diag.smtp_host}:{smtpDiag.diag.smtp_port} · cifrado {smtpDiag.diag.smtp_security || "?"} ·
                  remitente {smtpDiag.diag.smtp_from || "(vacío)"} · habilitado {String(smtpDiag.diag.smtp_enabled)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tipos agrupados por categoría */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-gray-400" />
          </div>
        ) : (
          ["usuarios", "operativas", "administrativas"].map((cat) => (
            <div key={cat}>
              <div className="flex items-center gap-2 mb-3">
                {(() => { const I = CATEGORY_META[cat].icon; return <I size={18} style={{ color: CATEGORY_META[cat].color }} />; })()}
                <h3 className="text-sm font-bold uppercase tracking-[0.18em] text-gray-700" style={{ fontFamily: "JetBrains Mono" }}>
                  {CATEGORY_META[cat].label}
                </h3>
                <span className="text-xs text-gray-400 font-mono">({(grouped[cat] || []).length})</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {(grouped[cat] || []).map((t) => (
                  <div
                    key={t.type}
                    className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition"
                    data-testid={`notif-card-${t.type}`}
                  >
                    <div className="font-mono text-[10px] uppercase tracking-wider text-[#0A3747] bg-[#E0E621] inline-block px-2 py-0.5 rounded mb-2">
                      {t.type}
                    </div>
                    <h4 className="font-semibold text-gray-900 text-sm">{t.subject.replace(/{{.*?}}/g, "...")}</h4>
                    <p className="text-xs text-gray-600 mt-1 mb-3">{t.description}</p>
                    <button
                      onClick={() => sendTest(t.type)}
                      disabled={sending === t.type || !testEmail || !hasPerm("M17.notifications.send")}
                      className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-[#0A3747] text-white text-xs font-semibold hover:bg-[#082B38] transition disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid={`send-test-${t.type}`}
                      title={hasPerm("M17.notifications.send") ? "Enviar correo de prueba" : "Requiere permiso M17.notifications.send"}
                    >
                      {sending === t.type ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                      Enviar prueba
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}

        {/* Log */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
            <h3 className="text-sm font-bold uppercase tracking-[0.18em] text-gray-700" style={{ fontFamily: "JetBrains Mono" }}>
              Log de envíos
            </h3>
            <div className="flex items-center gap-2">
              <Filter size={14} className="text-gray-400" />
              <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="text-xs border border-gray-300 rounded px-2 py-1" data-testid="filter-category">
                <option value="">Todas las categorías</option>
                <option value="usuarios">Usuarios</option>
                <option value="operativas">Operativas</option>
                <option value="administrativas">Administrativas</option>
              </select>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="text-xs border border-gray-300 rounded px-2 py-1" data-testid="filter-status">
                <option value="">Todos los estados</option>
                <option value="sent">Enviados</option>
                <option value="queued">En cola</option>
                <option value="failed">Fallidos</option>
              </select>
              <button onClick={fetchAll} className="p-1.5 rounded hover:bg-gray-100" title="Recargar" data-testid="refresh-log">
                <RefreshCw size={14} className="text-gray-600" />
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-mono text-gray-500">Fecha</th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-mono text-gray-500">Tipo</th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-mono text-gray-500">Destinatario</th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-mono text-gray-500">Estado</th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-mono text-gray-500">Intentos</th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-mono text-gray-500">Error</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr><td colSpan="6" className="text-center py-10 text-gray-400 text-sm">Sin envíos registrados aún.</td></tr>
                ) : pgLogs.paged.map((l) => {
                  const s = STATUS_BADGE[l.status] || STATUS_BADGE.queued;
                  const SIcon = s.icon;
                  return (
                    <tr key={l.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-2 text-xs text-gray-600 font-mono">
                        {l.sent_at ? new Date(l.sent_at).toLocaleString("es-EC") : new Date(l.queued_at).toLocaleString("es-EC")}
                      </td>
                      <td className="px-4 py-2 text-xs font-mono text-[#0A3747]">{l.notification_type}</td>
                      <td className="px-4 py-2 text-xs">{l.to_email}</td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${s.color}`}>
                          <SIcon size={10} /> {s.label}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-600">{l.attempts}</td>
                      <td className="px-4 py-2 text-xs text-red-700 truncate max-w-[280px]">{l.last_error || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {pgLogs.total > 0 && (
              <Pagination
                page={pgLogs.page}
                pageSize={pgLogs.pageSize}
                total={pgLogs.total}
                onPageChange={pgLogs.setPage}
                onPageSizeChange={pgLogs.setPageSize}
                testIdPrefix="notif-pagination"
              />
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function StatCard({ label, value, color = "text-gray-900" }) {
  return (
    <div className="bg-white border border-gray-200 rounded-md px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-mono">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${color}`} style={{ fontFamily: "Chivo" }}>{value}</div>
    </div>
  );
}
